import { inject, Injectable } from '@angular/core';
import { FilteredResult, RulesEngineService } from '@domain/rule-definitions';
import { ClientIntakeFormType, Form } from '@models/client-intake';
import { ClientIntakeResponse } from '@domain/intake-form-client-responses';
import { PortalEngineSelectors } from '../state/portal-engine.selectors';
import {
	buildContextData,
	DefaultValueContext,
	calculateFormProgressStats,
	getFormContent,
	orderFormContent,
	populateDefaultValuesFlat,
} from '../utils/portal-engine.utils';
import { select } from '@ngxs/store';
import { Matter, PersonMatterClientWithAssociations } from '@models/matter';
import { EngineFormItem } from '@models/client-portal/engine-client-intake';
import {
	EngineFormContent,
	FormProgressStats,
} from '../state/portal-engine.models';

@Injectable({ providedIn: 'root' })
export class PortalEngineService {
	rulesEngineService = inject(RulesEngineService);
	engineData_ = select(PortalEngineSelectors.$engineData);

	/**
	 * Run the portal engine
	 * @param formResponses - The form responses to use for the engine
	 * @returns The filtered results from the engine
	 */
	runPortalEngine(
		formResponses?: ClientIntakeResponse[],
	): FilteredResult<EngineFormItem>[] | null {
		const engineData = this.engineData_();
		if (!engineData) {
			return null;
		}
		const { context, currentForm, currentResponses, engineItems } = engineData;

		const responses = formResponses ? formResponses : currentResponses;

		const contextData = buildContextData(responses, context);

		if (!currentForm?.ruleDefinition || !engineItems.length || !contextData) {
			return null;
		}

		if (contextData.clients?.all?.length === 0 || !contextData.general) {
			return null;
		}

		return this.rulesEngineService.execute(
			currentForm.ruleDefinition,
			contextData,
			engineItems,
		);
	}

	/**
	 * Get the form progress stats
	 * @returns The form progress stats
	 */
	getFormProgressStats(): FormProgressStats {
		const formResponses = this.engineData_()?.currentResponses;
		const flatEngineForms = this.getFlatEngineForms(formResponses);
		return calculateFormProgressStats(formResponses, flatEngineForms);
	}

	/**
	 * Generate the portal engine form
	 * @param formResponses - The form responses to use for the form in client portal
	 * @returns The portal engine form
	 */
	generatePortalEngineForm(
		formResponses?: ClientIntakeResponse[],
	): Form[] | null {
		const flatFormContent = this.getFlatEngineForms(formResponses);
		if (!flatFormContent) {
			return null;
		}
		const forms = orderFormContent(flatFormContent);

		return forms;
	}

	/**
	 * Generate the portal engine preview forms with default values populated from matter/client data.
	 * Uses the unified populateDefaultValuesFlat to populate defaults on flat content before building hierarchy.
	 *
	 * @param clients - The clients to use for the forms
	 * @param matter - The matter to use for the forms
	 * @returns The portal engine preview forms with default values populated
	 */
	generatePortalEnginePreviewForms(
		clients: PersonMatterClientWithAssociations[],
		matter: Matter,
	): Form[] | null {
		const flatFormContent = this.getFlatEngineForms();
		if (!flatFormContent) {
			return null;
		}

		const formsWithDefaults = flatFormContent.map((formContent) => {
			let context: DefaultValueContext = { matter };

			if (formContent.formType === ClientIntakeFormType.CLIENT) {
				const client = clients.find((c) => c.id === formContent.id);
				if (client) {
					context = {
						matter,
						client,
						matterClient: client,
					};
				}
			}

			return populateDefaultValuesFlat(formContent, context);
		});

		return orderFormContent(formsWithDefaults);
	}

	/**
	 * Build the response page items for the response page
	 * @param responses - The responses to use for the response page
	 * @param clients - The clients to use for the response page
	 * @param matter - The matter to use for the response page
	 * @returns The response page items
	 */
	// buildResponsePageItems(responses: ClientIntakeResponse[], clients: PersonMatterClientWithAssociations[], matter: Matter): ReviewResponseItem[] {
	//     const engineForms = this.generatePortalEngineForm(responses);

	// }

	/**
	 * Get the flat engine forms
	 * @param formResponses - The form responses to use for the flat engine forms
	 * @returns The flat engine forms
	 */
	getFlatEngineForms(
		formResponses?: ClientIntakeResponse[],
	): EngineFormContent[] | null {
		const results = this.runPortalEngine(formResponses);
		if (!results) {
			return null;
		}
		return getFormContent(results);
	}
}
